import { useMemo } from "react";
import { ProgressRing } from "@/components/ui/ProgressRing";
import type { AgentCardItem, CardFinish } from "@/lib/api";
import { CARD_FINISHES, type CardFinishMeta } from "@/lib/cards";
import type { HomeAgent } from "@/lib/homeAgents";
import { AgentAvatar } from "./AgentAvatar";
import { FinishCardTile, type CardSelection } from "./FinishCardTile";
import { LockedTile } from "./LockedTile";

const RING_SIZE = 44;

/** Catalog entry the backend hasn't sent yet — render as a fresh locked slot. */
function fallbackItem(agentId: string, meta: CardFinishMeta): AgentCardItem {
  return {
    id: `${agentId}:${meta.finish}`,
    agentId,
    rarity: meta.finish,
    owned: false,
    count: 0,
    supplyCap: meta.supplyCap,
    mintedCount: 0,
    remainingSupply: meta.supplyCap,
  };
}

export type CollectionSort = "catalog" | "owned";

type AssistantSectionProps = {
  agent: HomeAgent;
  /** This assistant's catalog items from listAgentCards (any order, may be partial). */
  items: AgentCardItem[];
  /** Catalog № offset for this shelf — numbers stay with the card across sorts. */
  numberBase: number;
  sort: CollectionSort;
  onSelect: (selection: CardSelection) => void;
};

/**
 * One assistant's shelf — avatar with completion ring, owned/total count, and
 * the 8 finish tiles. Default order = catalog №; "owned" floats owned tiles
 * first (stable within groups). Unowned slots keep place + number (§2C).
 */
export function AssistantSection({ agent, items, numberBase, sort, onSelect }: AssistantSectionProps) {
  const byFinish = useMemo(
    () => new Map<CardFinish, AgentCardItem>(items.map((item) => [item.rarity, item])),
    [items],
  );

  const ownedCount = CARD_FINISHES.filter((meta) => byFinish.get(meta.finish)?.owned).length;
  const total = CARD_FINISHES.length;

  const metas =
    sort === "owned"
      ? [...CARD_FINISHES].sort(
          (a, b) =>
            Number(byFinish.get(b.finish)?.owned ?? false) -
            Number(byFinish.get(a.finish)?.owned ?? false),
        )
      : CARD_FINISHES;

  return (
    <section aria-label={`${agent.name} cards`} className="flex flex-col gap-3">
      <header className="flex items-center gap-3">
        <div className="relative shrink-0" style={{ width: RING_SIZE, height: RING_SIZE }}>
          <ProgressRing ratio={ownedCount / total} size={RING_SIZE} strokeWidth={3} active={ownedCount > 0} />
          <div className="absolute inset-[5px] overflow-hidden rounded-full border border-line bg-(--ty-bg-2)">
            <AgentAvatar agent={agent} className="size-full" />
          </div>
        </div>
        <div className="min-w-0">
          <h3 className="truncate font-display text-sm font-bold text-ink">
            {agent.name}
          </h3>
          <p className="font-mono text-[10px] font-medium uppercase tracking-[0.08em] tabular-nums text-ink-mute">
            {ownedCount} / {total} collected
          </p>
        </div>
      </header>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {metas.map((meta) => {
          const item = byFinish.get(meta.finish) ?? fallbackItem(agent.agentId, meta);
          const number = numberBase + CARD_FINISHES.indexOf(meta) + 1;
          return item.owned ? (
            <FinishCardTile
              key={meta.finish}
              agent={agent}
              meta={meta}
              item={item}
              number={number}
              onSelect={onSelect}
            />
          ) : (
            <LockedTile key={meta.finish} agent={agent} meta={meta} item={item} number={number} />
          );
        })}
      </div>
    </section>
  );
}
